import { useEffect } from 'react';
import './GustAnimation.css';

export default function GustAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle background
    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    // Get positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentRef.current.getBoundingClientRect();
    const battleBgRect = battleBg.getBoundingClientRect();

    // Calculate positions relative to battle background
    const startX = (playerRect.left + playerRect.width / 2) - battleBgRect.left;
    const startY = (playerRect.top + playerRect.height / 2) - battleBgRect.top;
    const endX = (opponentRect.left + opponentRect.width / 2) - battleBgRect.left;
    const endY = (opponentRect.top + opponentRect.height / 2) - battleBgRect.top;

    // We no longer need angle and distance since the tornado starts directly at opponent position

    // Track all created elements
    const elements = [];
    const timers = [];

    // Create wind-up effect
    const windUp = document.createElement('div');
    windUp.className = 'gust-wind-up';
    windUp.style.left = `${startX}px`;
    windUp.style.top = `${startY}px`;
    battleBg.appendChild(windUp);
    elements.push(windUp);

    // Player animation - flap/flutter
    playerEl.style.animation = 'gustPlayerFlutter 0.5s ease-in-out';

    // Create vertical gust storm that circles around the opponent
    const mainGustTimer = setTimeout(() => {
      // Wind buildup effect at player's position
      playerEl.style.animation = 'gustPlayerFlutter 0.8s ease-in-out';
      
      // Create vertical tornado at opponent's position
      const verticalTornado = document.createElement('div');
      verticalTornado.className = 'gust-vertical-tornado';
      verticalTornado.style.left = `${endX}px`;
      verticalTornado.style.top = `${endY}px`;
      battleBg.appendChild(verticalTornado);
      elements.push(verticalTornado);
      
      // Create circling mini-tornados around the opponent
      for (let i = 0; i < 4; i++) {
        const waveTimer = setTimeout(() => {
          // Create tornado that circles around opponent
          const circlingTornado = document.createElement('div');
          circlingTornado.className = 'gust-circling-tornado';
          circlingTornado.style.left = `${endX}px`;
          circlingTornado.style.top = `${endY}px`;
          // Each tornado starts at a different position around the circle
          circlingTornado.style.setProperty('--start-angle', `${i * 90}deg`);
          circlingTornado.style.setProperty('--rotation-speed', `${0.9 - i * 0.1}s`);
          circlingTornado.style.setProperty('--orbit-size', `${80 - i * 10}px`);
          circlingTornado.style.setProperty('--delay', `${i * 0.3}s`);
          
          battleBg.appendChild(circlingTornado);
          elements.push(circlingTornado);
          
          // Add swirl particles around the circling tornado
          for (let j = 0; j < 6; j++) {
            const swirlTimer = setTimeout(() => {
              if (!circlingTornado.isConnected) return;
              
              const swirl = document.createElement('div');
              swirl.className = 'gust-swirl';
              
              // Calculate position around the opponent
              const angle = (j / 6) * Math.PI * 2; // Around the circle
              const distance = 30 + Math.random() * 30; // Distance from opponent
              const waveX = endX + Math.cos(angle) * distance;
              const waveY = endY + Math.sin(angle) * distance;
              
              // Add some randomness to the swirl position
              const offsetX = (Math.random() - 0.5) * 30;
              const offsetY = (Math.random() - 0.5) * 30;
              
              swirl.style.left = `${waveX + offsetX}px`;
              swirl.style.top = `${waveY + offsetY}px`;
              swirl.style.setProperty('--rotation', `${Math.random() * 360}deg`);
              
              battleBg.appendChild(swirl);
              elements.push(swirl);
              
              // Auto-remove swirl after animation
              const swirlRemoveTimer = setTimeout(() => {
                if (swirl.isConnected) {
                  swirl.remove();
                }
              }, 600);
              
              timers.push(swirlRemoveTimer);
            }, j * 100); // Staggered release along path
            
            timers.push(swirlTimer);
          }
          
          // Auto-remove circling tornado after animation
          const waveRemoveTimer = setTimeout(() => {
            if (circlingTornado.isConnected) {
              circlingTornado.remove();
            }
          }, 1500);
          
          timers.push(waveRemoveTimer);
        }, i * 200); // Staggered wave release
        
        timers.push(waveTimer);
      }
    }, 400); // After wind-up
    
    timers.push(mainGustTimer);

    // Hit effect on opponent
    const hitTimer = setTimeout(() => {
      if (opponentEl) {
        // Create hit effect
        const hitEffect = document.createElement('div');
        hitEffect.className = 'gust-hit-effect';
        hitEffect.style.left = `${endX}px`;
        hitEffect.style.top = `${endY}px`;
        battleBg.appendChild(hitEffect);
        elements.push(hitEffect);
        
        // Make opponent shake
        opponentEl.style.animation = 'gustShake 0.6s ease-in-out';
        
        // Create multiple hit particles
        for (let i = 0; i < 12; i++) {
          const hitParticle = document.createElement('div');
          hitParticle.className = 'gust-hit-particle';
          hitParticle.style.left = `${endX}px`;
          hitParticle.style.top = `${endY}px`;
          
          // Random direction
          const particleAngle = Math.random() * Math.PI * 2;
          const particleDist = 10 + Math.random() * 40;
          hitParticle.style.setProperty('--dx', `${Math.cos(particleAngle) * particleDist}px`);
          hitParticle.style.setProperty('--dy', `${Math.sin(particleAngle) * particleDist}px`);
          
          // Random size and rotation
          const size = 8 + Math.random() * 16;
          hitParticle.style.setProperty('--particle-size', `${size}px`);
          hitParticle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
          
          battleBg.appendChild(hitParticle);
          elements.push(hitParticle);
          
          // Auto-remove particle after animation
          const particleRemoveTimer = setTimeout(() => {
            if (hitParticle.isConnected) {
              hitParticle.remove();
            }
          }, 500);
          
          timers.push(particleRemoveTimer);
        }
        
        // Reset opponent animation
        const opponentResetTimer = setTimeout(() => {
          if (opponentEl) {
            opponentEl.style.animation = '';
          }
        }, 600);
        
        timers.push(opponentResetTimer);
      }
    }, 800); // Hit after waves have traveled
    
    timers.push(hitTimer);

    // Complete animation
    const completeTimer = setTimeout(() => {
      // Reset player animation
      if (playerEl) {
        playerEl.style.animation = '';
      }
      
      // Call onComplete callback
      onComplete();
    }, 1500);
    
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset animations
      if (playerEl) {
        playerEl.style.animation = '';
      }
      if (opponentEl) {
        opponentEl.style.animation = '';
      }
      
      // Remove all elements
      elements.forEach(element => {
        if (element && element.isConnected) {
          element.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}
